/**
 * ULL-003: per-kind body for a NeedsAction `Expenditure` in the TriageModal —
 * the keyboard-driven sibling of the ledger's `PendingExpenditureActions` +
 * `ExpenditurePayeeInfoBody`. A born-Pending expenditure (bank-classify /
 * receipt-capture) now enters the modal queue (ULL-002 retired the born-Done
 * model); before this body it rendered an empty, actionless panel.
 *
 * Two sub-cases, by `actionNeeded`:
 *   - **Confirm** — the row is complete (below-threshold, or payee itemizable).
 *     A single Confirm button (Enter) flips Pending → Confirmed via
 *     `POST /api/finance/expenditures/{id}/confirm` (ULL-002).
 *   - **ProvideExpenditureInfo** — above-threshold with an incomplete payee
 *     address. The body shows the payee address fields (prefilled from the slim
 *     DTO) with a Save (Enter) that PUTs the address onto the payee
 *     `FinanceContact`, plus a Confirm to finalize as an incomplete draft (the
 *     ULL-004 file-time hard-fail is the backstop). A full address makes the
 *     payee itemizable, so the next sort-inbox refetch drops the row to Done.
 *
 * Contact `ConcurrencyVersion` defaults to 0 — the slim DTO doesn't carry the
 * token (same defensive default as `MissingInfoBody`); a 409 surfaces the
 * refresh prompt.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiFailure,
  updateFinanceContactRequest,
  type TriageItem,
  ErrorCode,
  isStaleWrite,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { AddressSuggestInput } from '@/components/finance/shared/classification';
import { formatAmount } from '@/components/finance/utils';
import {
  useConfirmExpenditure,
  useUpdateFinanceContact,
  ExpenditureConfirmError,
} from '@/hooks/queries';
import type { RegisterConfirmFn } from './PendingContributionBody';

interface ExpenditureBodyProps {
  item: Extract<TriageItem, { kind: 'Expenditure' }>;
  /** Fired after a successful Confirm or address Save — advances the modal. */
  onClassified: () => void;
  /** Registers / clears the body's Enter-key handler (Confirm or Save). */
  onRegisterConfirm: RegisterConfirmFn;
}

export function ExpenditureBody({
  item,
  onClassified,
  onRegisterConfirm,
}: ExpenditureBodyProps) {
  const { expenditure } = item;
  const needsPayeeInfo = item.actionNeeded === 'ProvideExpenditureInfo';

  const [address, setAddress] = useState(expenditure.payeeAddress ?? '');
  // Optional suite/apt line — prefilled so a save never blanks an existing value.
  // Never gates canSave (it's optional).
  const [addressLine2, setAddressLine2] = useState(expenditure.payeeAddress2 ?? '');
  const [city, setCity] = useState(expenditure.payeeCity ?? '');
  const [state, setState] = useState(expenditure.payeeState ?? '');
  const [zipCode, setZipCode] = useState(expenditure.payeeZipCode ?? '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutateAsync: confirmAsync, isPending: confirmPending } =
    useConfirmExpenditure();
  const { mutateAsync: updateContactAsync, isPending: savePending } =
    useUpdateFinanceContact();
  const isPending = confirmPending || savePending;

  // A full address (all four fields) is required to itemize the payee, so Save
  // stays disabled until the row would actually become Complete.
  const canSave = useMemo(
    () =>
      address.trim() !== '' &&
      city.trim() !== '' &&
      state.trim() !== '' &&
      zipCode.trim() !== '',
    [address, city, state, zipCode],
  );

  const handleConfirm = useCallback(async () => {
    setErrorMessage(null);
    try {
      await confirmAsync({
        row: expenditure,
      });
      onClassified();
    } catch (err) {
      if (
        err instanceof ExpenditureConfirmError && isStaleWrite(err)
      ) {
        setErrorMessage(
          'This expenditure was updated elsewhere — refresh to see the latest.',
        );
        return;
      }
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to confirm this expenditure.',
      );
    }
  }, [confirmAsync, expenditure.id, expenditure.concurrencyVersion, onClassified]);

  const handleSave = useCallback(async () => {
    setErrorMessage(null);
    try {
      await updateContactAsync({
        row: { id: expenditure.payeeContactId, concurrencyVersion: expenditure.payeeContactConcurrencyVersion },
        data: updateFinanceContactRequest({
          address: address.trim(),
          addressLine2: addressLine2.trim() || null,
          city: city.trim(),
          state: state.trim(),
          zipCode: zipCode.trim(),
          // Payee contact's real version — a hardcoded 0 409s for any payee
          // touched before, silently blocking the period close (ULL-003).
          concurrencyVersion: expenditure.payeeContactConcurrencyVersion,
        }),
      });
      // Fill-and-confirm: filling the payee address IS the treasurer's sign-off,
      // so a still-Pending (bank-classified / captured) expenditure is confirmed
      // in the same gesture and drops straight to Done — no redundant standalone
      // Confirm. The contact PUT doesn't bump the expenditure version, so its own
      // token is still valid. An already-Confirmed-but-incomplete row just became
      // Complete and needs no confirm. (The bare Confirm below still serves an
      // untouched, already-complete row.)
      if (expenditure.ledgerStatus === 'Pending') {
        await confirmAsync({
          row: expenditure,
        });
      }
      onClassified();
    } catch (err) {
      if (
        err instanceof ApiFailure && err.reason.kind === 'stale'
      ) {
        setErrorMessage(
          'This contact was updated elsewhere — refresh to see the latest.',
        );
        return;
      }
      // The address saved but the fold-in confirm lost a concurrency race — the
      // address IS persisted (durable), so this degrades to the row resurfacing
      // as a standalone Confirm.
      if (
        err instanceof ExpenditureConfirmError && isStaleWrite(err)
      ) {
        setErrorMessage(
          'Saved the payee address, but this expenditure was updated elsewhere — refresh, then confirm it.',
        );
        return;
      }
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to update the payee address.',
      );
    }
  }, [
    updateContactAsync,
    confirmAsync,
    expenditure.payeeContactId,
    expenditure.id,
    expenditure.ledgerStatus,
    expenditure.concurrencyVersion,
    expenditure.payeeContactConcurrencyVersion,
    address,
    addressLine2,
    city,
    state,
    zipCode,
    onClassified,
  ]);

  // Enter routes to Save on a ProvideExpenditureInfo row (when the address is
  // complete) and to Confirm otherwise. Ref-stabilized so the modal's
  // recreated `onClassified` doesn't loop the registration effect (see
  // PendingContributionBody for the rationale).
  const enterHandler = needsPayeeInfo ? handleSave : handleConfirm;
  const enterHandlerRef = useRef(enterHandler);
  useEffect(() => {
    enterHandlerRef.current = enterHandler;
  }, [enterHandler]);

  const enterEnabled = needsPayeeInfo ? canSave && !isPending : !isPending;
  useEffect(() => {
    if (!enterEnabled) {
      onRegisterConfirm(null);
      return;
    }
    onRegisterConfirm({ fn: () => enterHandlerRef.current() });
    return () => onRegisterConfirm(null);
  }, [enterEnabled, onRegisterConfirm]);

  return (
    <div data-testid="expenditure-body" className="flex flex-col gap-4">
      <div className="rounded-md border bg-muted/30 p-3 text-sm">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-mono text-xs text-muted-foreground">
            {expenditure.date}
          </span>
          <span className="font-semibold">{formatAmount(expenditure.amount)}</span>
          <span className="text-foreground">{expenditure.payeeName}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {needsPayeeInfo
            ? 'Over the itemization limit, so this needs the payee address. Add it below to finish.'
            : 'Confirm this expenditure to finalize it for filing.'}
        </p>
      </div>

      {needsPayeeInfo && (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Street address</span>
            <AddressSuggestInput
              data-testid="expenditure-body-address"
              value={address}
              disabled={isPending}
              className="rounded border px-2 py-1 text-sm"
              onChange={setAddress}
              onPick={(p) => {
                setCity(p.city);
                setState(p.state);
                setZipCode(p.zip);
              }}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Address line 2</span>
            <input
              data-testid="expenditure-body-address2"
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              disabled={isPending}
              className="rounded border px-2 py-1 text-sm"
              placeholder="Apt, suite, floor (optional)"
            />
          </label>
          <div className="flex gap-3">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="font-medium">City</span>
              <input
                data-testid="expenditure-body-city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={isPending}
                className="rounded border px-2 py-1 text-sm"
              />
            </label>
            <label className="flex w-20 flex-col gap-1 text-sm">
              <span className="font-medium">State</span>
              <input
                data-testid="expenditure-body-state"
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                disabled={isPending}
                className="rounded border px-2 py-1 text-sm"
              />
            </label>
            <label className="flex w-28 flex-col gap-1 text-sm">
              <span className="font-medium">ZIP</span>
              <input
                data-testid="expenditure-body-zip"
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                disabled={isPending}
                className="rounded border px-2 py-1 text-sm"
              />
            </label>
          </div>
        </div>
      )}

      {errorMessage && (
        <div
          role="alert"
          data-testid="expenditure-body-error"
          className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {errorMessage}
        </div>
      )}

      {needsPayeeInfo ? (
        // One primary path: fill the address and finish. "Save & confirm" says
        // out loud that clicking persists the typed address AND finalizes the
        // row (fill = the treasurer's sign-off). Full-width so it isn't missed
        // tucked in the bottom-right after the address is entered.
        <div className="border-t pt-3">
          <Button
            type="button"
            className="w-full"
            data-testid="expenditure-body-save"
            disabled={isPending || !canSave}
            onClick={handleSave}
          >
            Save & confirm
          </Button>
        </div>
      ) : (
        // Already complete — nothing to save, so the honest label is "Confirm".
        <div className="border-t pt-3">
          <Button
            type="button"
            className="w-full"
            data-testid="expenditure-body-confirm"
            disabled={isPending}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </div>
      )}
    </div>
  );
}
